"use client"

import Link from "next/link"
import { useRouter } from "next/navigation"
import { type FormEvent, useRef, useState } from "react"
import { z } from "zod"
import { submitCommand } from "../lib/api-client.ts"
import { safeNextRoute } from "../lib/safe-route.ts"
import type { UserView } from "../lib/user-view.ts"
import styles from "./my-support.module.css"
import { PaginationControls } from "./pagination-controls.tsx"

type SupportViewModel = Extract<UserView, { readonly kind: "support" }>
const inquiryCategories = ["일반 문의", "포인트 전환", "상품 교환", "계정·휴대전화", "기타 문의"] as const
type InquiryCategory = (typeof inquiryCategories)[number]
const inquirySchema = z.strictObject({ message: z.string().trim().min(10).max(2000), subject: z.enum(inquiryCategories) })
const messageAuthor = { ADMIN: "CashHub 답변", SYSTEM: "CashHub 안내", USER: "내 문의" } as const
const messageTime = new Intl.DateTimeFormat("ko-KR", { dateStyle: "medium", timeStyle: "short" })
const supportPageSize = 5

export const SupportView = ({ view }: { readonly view: SupportViewModel }) => {
  const router = useRouter()
  const [message, setMessage] = useState("")
  const [category, setCategory] = useState<InquiryCategory>("일반 문의")
  const [error, setError] = useState("")
  const [pending, setPending] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const messageInput = useRef<HTMLTextAreaElement>(null)

  if (view.selectedCase !== undefined) {
    const hasAdminReply = view.selectedCase.messages.some((item) => item.author === "ADMIN")
    return (
      <div className={styles["supportCase"]} data-support-case>
        <p className={styles["supportCaseStatus"]}>{view.selectedCase.status === "ANSWERED" ? "답변 완료" : "접수됨"}</p>
        <h2>{view.selectedCase.subject}</h2>
        {view.selectedCase.messages.length === 0 ? (
          <p className={styles["supportFallbackMessage"]}>{view.selectedCase.message}</p>
        ) : (
          <ol aria-label="문의 대화" className={styles["supportMessages"]}>
            {view.selectedCase.messages.map((item) => (
              <li className={`${styles["supportMessage"]} ${item.author === "USER" ? styles["supportMessageUser"] : styles["supportMessageCashHub"]}`} key={item.messageId}>
                <div className={styles["supportMessageMeta"]}>
                  <strong>{messageAuthor[item.author]}</strong>
                  <time dateTime={item.createdAt}>{messageTime.format(new Date(item.createdAt))}</time>
                </div>
                <p>{item.body}</p>
              </li>
            ))}
          </ol>
        )}
        {hasAdminReply || view.selectedCase.status === "ANSWERED" ? null : <p className={styles["supportWaiting"]}>문의가 접수되었습니다. CashHub 관리자가 확인 후 답변드릴게요.</p>}
        <Link className="button secondary" href="/support/inquiries">
          문의 목록
        </Link>
      </div>
    )
  }

  const submit = async (): Promise<void> => {
    const inquiry = inquirySchema.safeParse({ message, subject: category })
    if (!inquiry.success) {
      setError("문의 내용은 10자 이상 2,000자 이하로 입력해 주세요.")
      queueMicrotask(() => messageInput.current?.focus())
      return
    }
    setPending(true)
    setError("")
    try {
      const receipt = await submitCommand("/web/support/inquiries", inquiry.data)
      const nextRoute = safeNextRoute(receipt.nextRoute)
      if (nextRoute === null || !nextRoute.startsWith("/support/inquiries/")) {
        setError("문의 결과를 안전하게 열 수 없습니다. 문의 목록에서 다시 확인해 주세요.")
        return
      }
      router.push(nextRoute)
    } finally {
      setPending(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil(view.cases.length / supportPageSize))
  const pageCases = view.cases.slice((currentPage - 1) * supportPageSize, currentPage * supportPageSize)

  return (
    <div className={styles["supportLayout"]} data-support-layout>
      <section aria-label="새 문의 작성" className={styles["supportComposer"]} data-support-compose>
        <form
          className={styles["supportForm"]}
          data-support-form
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            void submit()
          }}
        >
          <label htmlFor="support-category">문의 유형</label>
          <span className={styles["supportSelect"]}>
            <select id="support-category" onChange={(event) => setCategory(event.currentTarget.value as InquiryCategory)} value={category}>
              {inquiryCategories.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </span>
          <span className={styles["supportMessageLabel"]}>
            <label htmlFor="support-message">문의 내용</label>
            <output htmlFor="support-message">{message.length.toLocaleString("ko-KR")} / 2,000</output>
          </span>
          <textarea
            aria-describedby={error.length === 0 ? "support-message-help" : "support-message-help support-inquiry-error"}
            aria-invalid={error.length > 0}
            id="support-message"
            maxLength={2000}
            onChange={(event) => {
              setMessage(event.target.value)
              if (error.length > 0) setError("")
            }}
            placeholder="문의 내용을 자세히 적어 주세요. 개인정보와 인증번호는 입력하지 마세요."
            ref={messageInput}
            rows={7}
            value={message}
          />
          <p className={styles["supportHelp"]} id="support-message-help">
            문의를 접수하면 처리 상태와 답변을 아래에서 확인할 수 있어요.
          </p>
          <button aria-busy={pending} className="button primary" disabled={pending || message.trim().length < 10} type="submit">
            {pending ? "문의 접수 중" : "문의 접수"}
          </button>
          {error.length === 0 ? null : (
            <p aria-live="assertive" className={styles["supportError"]} data-support-error id="support-inquiry-error">
              {error}
            </p>
          )}
        </form>
      </section>
      <section aria-labelledby="support-history-title" className={styles["supportHistory"]}>
        <div className={styles["sectionHeading"]}>
          <h2 id="support-history-title">내 문의</h2>
          <span>최근 {view.cases.length.toLocaleString("ko-KR")}건</span>
        </div>
        {view.cases.length === 0 ? (
          <p className={styles["supportEmpty"]}>아직 접수한 문의가 없습니다.</p>
        ) : (
          <ul className={styles["supportCaseList"]}>
            {pageCases.map((supportCase) => {
              const firstUserMessage = supportCase.messages.find((item) => item.author === "USER")
              const question = firstUserMessage?.body ?? supportCase.message
              return (
                <li className={styles["supportCaseItem"]} key={supportCase.id}>
                  <Link className={styles["supportCaseLink"]} href={`/support/inquiries/${supportCase.id}`}>
                    <span className={styles["supportCaseHeading"]}>
                      <strong>{supportCase.subject}</strong>
                      <span>{supportCase.status === "ANSWERED" ? "답변 완료" : "접수됨"}</span>
                    </span>
                    <span className={styles["supportCaseQuestion"]}>{question}</span>
                    {firstUserMessage === undefined ? null : <time dateTime={firstUserMessage.createdAt}>{messageTime.format(new Date(firstUserMessage.createdAt))}</time>}
                  </Link>
                </li>
              )
            })}
          </ul>
        )}
        <PaginationControls currentPage={currentPage} label="문의 목록 페이지" onPageChange={setCurrentPage} totalPages={totalPages} />
      </section>
    </div>
  )
}
